import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Spinner, Button, Modal, Form, Row, Col, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getOrder } from '../../../services/OrdersService';
import { getInvoices, deleteInvoice, deleteAllTestInvoices } from '../../../services/InvoicesService';
import { hasPermission } from '../../../services/AuthService';
import Swal from 'sweetalert2';
import { getBusinessProfile, updateBusinessProfile } from '../../../services/SettingsService';
import { formatDateForLanguage, formatEuroForLanguage } from '../../../utils/i18nHelpers';
import OrderInvoice from '../Orders/OrderInvoice';

function formatDate(d) {
    return formatDateForLanguage(d, { day: '2-digit', month: 'short', year: 'numeric' });
}

const EMPTY_PROFILE = {
    business_name: '',
    street_address: '',
    postcode_city: '',
    email: '',
    phone: '',
    kvk_number: '',
    btw_nummer: '',
    logo_base64: '',
    btw_rate: '21',
    kor_exemption: false,
    payment_terms: 'Betaling binnen 14 dagen na factuurdatum.',
    invoice_notes: '',
    invoice_notes_font_size: 'normal',
    invoice_notes_bold: false,
    invoice_notes_align: 'left',
    invoice_notes_block_position: 'left',
    payment_terms_font_size: 'normal',
    payment_terms_bold: false,
    payment_terms_align: 'left',
    payment_terms_block_position: 'left',
    business_info_block_position: 'right',
    business_info_align: 'right',
    free_delivery_threshold: 150,
};

const FONT_SIZE_MAP = { small: 12, normal: 14, large: 16 };

const ALIGN_ICONS = [
    ['left',   'fa-align-left'],
    ['center', 'fa-align-center'],
    ['right',  'fa-align-right'],
];

// Two-row alignment controls: block position + text alignment
function AlignmentControls({ blockPositionKey, alignKey, profile, onChange, t }) {
    return (
        <div className="mb-2">
            <div className="d-flex align-items-center gap-2 mb-1">
                <span className="text-muted small" style={{ minWidth: 112, flexShrink: 0 }}>
                    {t('invoices.editForm.blockPosition')}
                </span>
                <div className="d-flex gap-1">
                    {ALIGN_ICONS.map(([val, icon]) => (
                        <Button key={val} size="sm"
                            variant={profile[blockPositionKey] === val ? 'primary' : 'outline-secondary'}
                            onClick={() => onChange(blockPositionKey, val)}
                            style={{ minWidth: 34 }}
                            title={val}
                        ><i className={`fa-solid ${icon}`} /></Button>
                    ))}
                </div>
            </div>
            <div className="d-flex align-items-center gap-2">
                <span className="text-muted small" style={{ minWidth: 112, flexShrink: 0 }}>
                    {t('invoices.editForm.textAlignment')}
                </span>
                <div className="d-flex gap-1">
                    {ALIGN_ICONS.map(([val, icon]) => (
                        <Button key={val} size="sm"
                            variant={profile[alignKey] === val ? 'primary' : 'outline-secondary'}
                            onClick={() => onChange(alignKey, val)}
                            style={{ minWidth: 34 }}
                            title={val}
                        ><i className={`fa-solid ${icon}`} /></Button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Inline formatting toolbar: size + bold, then block position + text alignment rows
function FormatToolbar({ sizeKey, boldKey, blockPositionKey, alignKey, profile, onChange, t }) {
    const sizes = [
        ['small',  t('invoices.editForm.notesSizeSmall')],
        ['normal', t('invoices.editForm.notesSizeNormal')],
        ['large',  t('invoices.editForm.notesSizeLarge')],
    ];
    return (
        <div className="mb-2">
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <div className="d-flex gap-1">
                    {sizes.map(([val, label]) => (
                        <Button key={val} size="sm"
                            variant={profile[sizeKey] === val ? 'primary' : 'outline-secondary'}
                            onClick={() => onChange(sizeKey, val)}
                            style={{ minWidth: 58 }}
                        >{label}</Button>
                    ))}
                </div>
                <div style={{ width: 1, height: 24, background: '#dee2e6' }} />
                <Button size="sm"
                    variant={profile[boldKey] ? 'primary' : 'outline-secondary'}
                    onClick={() => onChange(boldKey, !profile[boldKey])}
                    style={{ fontWeight: 700, minWidth: 34 }}
                    title={t('invoices.editForm.notesBold')}
                >B</Button>
            </div>
            <AlignmentControls
                blockPositionKey={blockPositionKey}
                alignKey={alignKey}
                profile={profile}
                onChange={onChange}
                t={t}
            />
        </div>
    );
}

export default function Invoices() {
    const { t, i18n } = useTranslation();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
    const [filterTotalMin, setFilterTotalMin] = useState('');
    const [filterTotalMax, setFilterTotalMax] = useState('');
    const [filterKind, setFilterKind] = useState('all'); // all | real | test
    const [filterMarketing, setFilterMarketing] = useState('all'); // all | sales | marketing
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedDocId, setSelectedDocId] = useState(null);

    // Edit invoice data modal state
    const [editInvoiceDataShow, setEditInvoiceDataShow] = useState(false);
    const [profile, setProfile] = useState(EMPTY_PROFILE);
    const [saving, setSaving] = useState(false);
    const [logoPreview, setLogoPreview] = useState('');
    const logoInputRef = useRef(null);

    const load = async () => {
        try {
            setLoading(true);
            const res = await getInvoices();
            setInvoices(res.data || []);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('invoices.failedLoad');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openEditInvoiceData = async () => {
        try {
            const res = await getBusinessProfile();
            const data = { ...EMPTY_PROFILE, ...(res.data || {}) };
            setProfile(data);
            setLogoPreview(data.logo_base64 || '');
        } catch {
            setProfile(EMPTY_PROFILE);
            setLogoPreview('');
        }
        setEditInvoiceDataShow(true);
    };

    const handleFieldChange = (field, value) => {
        setProfile((prev) => ({ ...prev, [field]: value }));
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error(t('invoices.editForm.logoInvalidType'));
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const b64 = ev.target.result;
            setLogoPreview(b64);
            handleFieldChange('logo_base64', b64);
        };
        reader.readAsDataURL(file);
    };

    const handleLogoRemove = () => {
        setLogoPreview('');
        handleFieldChange('logo_base64', '');
        if (logoInputRef.current) logoInputRef.current.value = '';
    };

    const saveProfile = async () => {
        if (!profile.business_name?.trim()) {
            toast.error(t('invoices.editForm.businessNameRequired'));
            return;
        }
        setSaving(true);
        try {
            await updateBusinessProfile(profile);
            toast.success(t('invoices.invoiceDataSaved'));
            setEditInvoiceDataShow(false);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('invoices.failedSave');
            if (err.response?.status === 401) {
                toast.error(t('invoices.loginAgain'));
            } else {
                toast.error(typeof msg === 'string' ? msg : t('invoices.failedSave'));
            }
        } finally {
            setSaving(false);
        }
    };

    const filteredInvoices = useMemo(() => {
        let filtered = invoices;
        if (filterKind === 'real') filtered = filtered.filter((inv) => !inv.is_test);
        if (filterKind === 'test') filtered = filtered.filter((inv) => inv.is_test);
        if (filterMarketing === 'marketing') filtered = filtered.filter((inv) => inv.is_marketing);
        if (filterMarketing === 'sales') filtered = filtered.filter((inv) => !inv.is_marketing);
        if (filterPaymentStatus) {
            filtered = filtered.filter((inv) => (inv.order_payment_status || inv.payment_status) === filterPaymentStatus);
        }
        if (filterTotalMin) {
            const min = parseFloat(filterTotalMin);
            if (!isNaN(min)) filtered = filtered.filter((inv) => (inv.total || 0) >= min);
        }
        if (filterTotalMax) {
            const max = parseFloat(filterTotalMax);
            if (!isNaN(max)) filtered = filtered.filter((inv) => (inv.total || 0) <= max);
        }
        const query = searchQuery.trim().toLowerCase();
        if (query) {
            filtered = filtered.filter((inv) => {
                if (String(inv.invoice_number || '').toLowerCase().includes(query)) return true;
                if (String(inv.order_id || '').toLowerCase().includes(query)) return true;
                if (String(inv.buyer_name || '').toLowerCase().includes(query)) return true;
                if (inv.invoice_date && formatDate(inv.invoice_date).toLowerCase().includes(query)) return true;
                if (inv.delivery_date && formatDate(inv.delivery_date).toLowerCase().includes(query)) return true;
                if (inv.order_date && formatDate(inv.order_date).toLowerCase().includes(query)) return true;
                return false;
            });
        }
        return filtered;
    }, [invoices, searchQuery, filterPaymentStatus, filterTotalMin, filterTotalMax, filterKind, filterMarketing]);

    const handleViewInvoice = async (invoice) => {
        try {
            const response = await getOrder(invoice.order_id);
            setSelectedDocId(invoice.is_legacy ? null : invoice.id);
            setSelectedInvoice(response.data);
        } catch {
            toast.error(t('invoices.failedLoadOrderDetails'));
        }
    };

    const hasTestInvoices = invoices.some((inv) => inv.is_test);
    const canDeleteTest = hasPermission('invoices.delete');
    const canDeleteAllTest = hasPermission('invoices.manage');

    const handleDeleteTestInvoice = async (inv) => {
        const result = await Swal.fire({
            title: t('invoices.test.deleteTitle'),
            text: t('invoices.test.deleteText', { number: inv.invoice_number }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: t('invoices.test.deleteConfirm'),
        });
        if (!result.isConfirmed) return;
        try {
            await deleteInvoice(inv.id);
            toast.success(t('invoices.test.deleted'));
            load();
        } catch (err) {
            const key = err.response?.data?.error_key;
            toast.error((key && t(key)) || err.response?.data?.error || t('invoices.test.deleteFailed'));
        }
    };

    const handleDeleteAllTest = async () => {
        const result = await Swal.fire({
            title: t('invoices.test.deleteAllTitle'),
            text: t('invoices.test.deleteAllText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: t('invoices.test.deleteAllConfirm'),
        });
        if (!result.isConfirmed) return;
        try {
            const res = await deleteAllTestInvoices();
            toast.success(t('invoices.test.deletedAll', { count: res.data?.deleted ?? 0 }));
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || t('invoices.test.deleteFailed'));
        }
    };

    const getPaymentStatusBadge = (status) => ({ paid: 'success', unpaid: 'secondary', partial: 'warning' }[status] || 'secondary');
    const getPaymentStatusLabel = (status) => ({ paid: t('invoices.paid'), unpaid: t('invoices.unpaid'), partial: t('invoices.partial') }[status] || status);

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" className="text-primary" />
                    <p className="mt-2 text-muted mb-0">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h4 className="heading mb-0">{t('invoices.title')}</h4>
                                <span className="text-muted">{t('invoices.subtitle')}</span>
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="outline-primary" size="sm" onClick={openEditInvoiceData}>
                                    <i className="fa-solid fa-pen me-1" /> {t('invoices.editInvoiceData')}
                                </Button>
                                {canDeleteAllTest && hasTestInvoices && (
                                    <Button variant="outline-danger" size="sm" onClick={handleDeleteAllTest}>
                                        <i className="fa-solid fa-trash me-1" /> {t('invoices.test.deleteAll')}
                                    </Button>
                                )}
                                <Link to="/orders/create" className="btn btn-primary btn-sm">
                                    <i className="fa-solid fa-plus me-1" /> {t('invoices.newOrder')}
                                </Link>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('invoices.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </Col>
                                <Col md={1}>
                                    <Form.Select aria-label={t('invoices.test.filterLabel')} value={filterKind} onChange={(e) => setFilterKind(e.target.value)}>
                                        <option value="all">{t('invoices.test.filterAll')}</option>
                                        <option value="real">{t('invoices.test.filterReal')}</option>
                                        <option value="test">{t('invoices.test.filterTest')}</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Select aria-label={t('invoices.marketing.filterLabel')} value={filterMarketing} onChange={(e) => setFilterMarketing(e.target.value)}>
                                        <option value="all">{t('invoices.marketing.filterAll')}</option>
                                        <option value="sales">{t('invoices.marketing.filterSales')}</option>
                                        <option value="marketing">{t('invoices.marketing.filterMarketing')}</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Select value={filterPaymentStatus} onChange={(e) => setFilterPaymentStatus(e.target.value)}>
                                        <option value="">{t('invoices.allPaymentStatuses')}</option>
                                        <option value="paid">{t('invoices.paid')}</option>
                                        <option value="unpaid">{t('invoices.unpaid')}</option>
                                        <option value="partial">{t('invoices.partial')}</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Form.Control type="number" step="0.01" min="0" placeholder={t('invoices.minTotal')} value={filterTotalMin} onChange={(e) => setFilterTotalMin(e.target.value)} style={{ flex: 1 }} />
                                        <span className="text-muted">-</span>
                                        <Form.Control type="number" step="0.01" min="0" placeholder={t('invoices.maxTotal')} value={filterTotalMax} onChange={(e) => setFilterTotalMax(e.target.value)} style={{ flex: 1 }} />
                                    </div>
                                </Col>
                                <Col md={3}>
                                    {(filterPaymentStatus || filterTotalMin || filterTotalMax) && (
                                        <Button variant="light" size="sm" onClick={() => { setFilterPaymentStatus(''); setFilterTotalMin(''); setFilterTotalMax(''); }}>
                                            <i className="fa-solid fa-times me-1" /> {t('invoices.clearFilters')}
                                        </Button>
                                    )}
                                </Col>
                            </Row>

                            {filteredInvoices.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">{t('invoices.noInvoices')}</p>
                                    {invoices.length === 0 && <p className="text-muted small">{t('invoices.createOrderToGenerateInvoice')}</p>}
                                </div>
                            ) : (
                                <Table responsive bordered className="invoices-main-table">
                                    <thead>
                                        <tr>
                                            <th>{t('invoices.columns.invoiceNumber')}</th>
                                            <th>{t('invoices.columns.orderNumber')}</th>
                                            <th>{t('invoices.columns.buyer')}</th>
                                            <th>{t('invoices.columns.invoiceDate')}</th>
                                            <th>{t('invoices.columns.deliveryDate')}</th>
                                            <th>{t('invoices.columns.total')}</th>
                                            <th>{t('invoices.columns.amountPaid')}</th>
                                            <th>{t('invoices.columns.remaining')}</th>
                                            <th>{t('invoices.columns.paymentStatus')}</th>
                                            <th>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInvoices.map((inv) => (
                                            <tr key={inv.id}>
                                                <td>
                                                    <strong>{inv.invoice_number}</strong>
                                                    {(inv.kind === 'credit_note' || inv.kind === 'marketing_credit') && <span className="badge order-flag-badge order-flag-badge--credit">{t('invoices.creditNoteBadge')}</span>}
                                                    {inv.is_marketing && <span className="badge order-flag-badge order-flag-badge--marketing" data-testid="marketing-badge">{t('invoices.marketing.badge')}</span>}
                                                    {inv.is_legacy && <span className="badge order-flag-badge order-flag-badge--test">{t('invoices.legacy')}</span>}
                                                    {inv.is_test && <span className="badge order-flag-badge order-flag-badge--review" data-testid="test-badge">{t('invoices.test.badge')}</span>}
                                                    {inv.is_marketing && <small className="d-block text-muted" data-testid="marketing-not-pushed">{t('invoices.marketing.notPushed')}</small>}
                                                </td>
                                                <td><Link to={`/orders/${inv.order_id}`}>#{inv.order_number || inv.order_id}</Link></td>
                                                <td>{inv.buyer_name || '-'}</td>
                                                <td>{formatDate(inv.invoice_date)}</td>
                                                <td>{formatDate(inv.delivery_date)}</td>
                                                <td>{formatEuroForLanguage(inv.total)}</td>
                                                <td>{formatEuroForLanguage(inv.order_amount_paid != null ? inv.order_amount_paid : inv.amount_paid)}</td>
                                                <td>{formatEuroForLanguage(inv.order_amount_remaining != null ? inv.order_amount_remaining : inv.amount_remaining)}</td>
                                                <td>
                                                    <span className={`badge bg-${getPaymentStatusBadge(inv.order_payment_status || inv.payment_status)}`}>
                                                        {getPaymentStatusLabel(inv.order_payment_status || inv.payment_status)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Button variant="primary" size="sm" onClick={() => handleViewInvoice(inv)} title={t('invoices.viewInvoice')}>
                                                            <i className="fa-solid fa-file-invoice me-1" /> {t('invoices.viewInvoice')}
                                                        </Button>
                                                        <Link to={`/orders/${inv.order_id}`} className="btn btn-secondary btn-sm" title={t('invoices.viewOrder')}>
                                                            <i className="fa-solid fa-list me-1" /> {t('invoices.viewOrder')}
                                                        </Link>
                                                        {inv.is_test && canDeleteTest && (
                                                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTestInvoice(inv)} title={t('invoices.test.deleteTitle')}>
                                                                <i className="fa-solid fa-trash" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}

                            {filteredInvoices.length > 0 && (
                                <div className="mt-3 text-muted small">
                                    {t('invoices.showing', { shown: filteredInvoices.length, total: invoices.length })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* ── Invoice Preview Modal ─────────────────────────────────────── */}
                    <Modal show={selectedInvoice !== null} onHide={() => { setSelectedInvoice(null); setSelectedDocId(null); }} size="xl" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{t('invoices.invoicePreview')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                            {selectedInvoice && <OrderInvoice order={selectedInvoice} invoiceId={selectedDocId} />}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setSelectedInvoice(null)}>{t('common.close')}</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* ── Edit Invoice Data Modal ───────────────────────────────────── */}
                    <Modal show={editInvoiceDataShow} onHide={() => setEditInvoiceDataShow(false)} size="lg" centered scrollable>
                        <Modal.Header closeButton style={{ borderBottom: '2px solid #f0e6ff' }}>
                            <Modal.Title>
                                <i className="fa-solid fa-building me-2 text-primary" />
                                {t('invoices.editInvoiceData')}
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body className="px-4">
                            {/* ── Section: Business Info ── */}
                            <div className="mb-4">
                                <h6 className="text-uppercase fw-bold text-primary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.08em' }}>
                                    <i className="fa-solid fa-store me-1" /> {t('invoices.editForm.sectionBusiness')}
                                </h6>
                                <p className="text-muted small mb-3">{t('invoices.editForm.sectionBusinessHint')}</p>

                                {/* Logo upload */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">
                                        {t('invoices.editForm.logo')} <span className="text-muted fw-normal">— {t('invoices.editForm.optional')}</span>
                                    </Form.Label>
                                    <div className="d-flex align-items-center gap-3">
                                        {logoPreview ? (
                                            <div className="position-relative" style={{ display: 'inline-block' }}>
                                                <img
                                                    src={logoPreview}
                                                    alt="Logo preview"
                                                    style={{ height: 64, maxWidth: 180, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 6, padding: 4, background: '#fafafa' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger position-absolute"
                                                    style={{ top: -8, right: -8, padding: '0 5px', fontSize: 11, lineHeight: '18px' }}
                                                    onClick={handleLogoRemove}
                                                    title={t('invoices.editForm.logoRemove')}
                                                >
                                                    <i className="fa-solid fa-times" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ width: 100, height: 64, border: '2px dashed #dee2e6', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                                                <i className="fa-solid fa-image text-muted" style={{ fontSize: 22 }} />
                                            </div>
                                        )}
                                        <div>
                                            <Button variant="outline-secondary" size="sm" onClick={() => logoInputRef.current?.click()}>
                                                <i className="fa-solid fa-upload me-1" /> {t('invoices.editForm.logoUpload')}
                                            </Button>
                                            <div className="text-muted small mt-1">{t('invoices.editForm.logoHint')}</div>
                                        </div>
                                        <input ref={logoInputRef} type="file" accept="image/*" className="d-none" onChange={handleLogoUpload} />
                                    </div>
                                </Form.Group>

                                {/* Business name */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">
                                        {t('invoices.editForm.businessName')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('invoices.editForm.businessNamePlaceholder')}
                                        value={profile.business_name}
                                        onChange={(e) => handleFieldChange('business_name', e.target.value)}
                                    />
                                </Form.Group>

                                <Row className="mb-3">
                                    <Col md={7}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t('invoices.editForm.streetAddress')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder={t('invoices.editForm.streetAddressPlaceholder')}
                                                value={profile.street_address}
                                                onChange={(e) => handleFieldChange('street_address', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={5}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t('invoices.editForm.postcodeCity')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder={t('invoices.editForm.postcodeCityPlaceholder')}
                                                value={profile.postcode_city}
                                                onChange={(e) => handleFieldChange('postcode_city', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t('invoices.editForm.email')}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="info@bedrijfsnaam.nl"
                                                value={profile.email}
                                                onChange={(e) => handleFieldChange('email', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t('invoices.editForm.phone')}</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                placeholder="06 12 34 56 78"
                                                value={profile.phone}
                                                onChange={(e) => handleFieldChange('phone', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t('invoices.editForm.kvkNumber')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="12345678"
                                                value={profile.kvk_number}
                                                onChange={(e) => handleFieldChange('kvk_number', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t('invoices.editForm.btwNummer')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="NL123456789B01"
                                                value={profile.btw_nummer}
                                                onChange={(e) => handleFieldChange('btw_nummer', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Business info block layout */}
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-semibold">{t('invoices.editForm.businessInfoLayout')}</Form.Label>
                                    <AlignmentControls
                                        blockPositionKey="business_info_block_position"
                                        alignKey="business_info_align"
                                        profile={profile}
                                        onChange={handleFieldChange}
                                        t={t}
                                    />
                                </Form.Group>
                            </div>

                            <hr className="my-4" />

                            {/* ── Section: Legal / Tax Settings ── */}
                            <div className="mb-3">
                                <h6 className="text-uppercase fw-bold text-primary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.08em' }}>
                                    <i className="fa-solid fa-file-contract me-1" /> {t('invoices.editForm.sectionLegal')}
                                </h6>

                                {/* KOR toggle */}
                                <Form.Group className="mb-3">
                                    <div className="d-flex align-items-start gap-3 p-3 rounded" style={{ background: profile.kor_exemption ? '#fff8e1' : '#f8f9fa', border: `1px solid ${profile.kor_exemption ? '#ffe082' : '#dee2e6'}`, transition: 'all 0.2s' }}>
                                        <Form.Check
                                            type="switch"
                                            id="kor-exemption-switch"
                                            checked={profile.kor_exemption}
                                            onChange={(e) => handleFieldChange('kor_exemption', e.target.checked)}
                                            style={{ marginTop: 2 }}
                                        />
                                        <div>
                                            <Form.Label htmlFor="kor-exemption-switch" className="fw-semibold mb-0" style={{ cursor: 'pointer' }}>
                                                {t('invoices.editForm.korExemption')}
                                            </Form.Label>
                                            <div className="text-muted small mt-1">{t('invoices.editForm.korExemptionHint')}</div>
                                            {profile.kor_exemption && (
                                                <div className="mt-2 px-3 py-2 rounded" style={{ background: '#fff3cd', border: '1px solid #ffc107', fontSize: 13, color: '#856404' }}>
                                                    <i className="fa-solid fa-circle-info me-1" />
                                                    {t('invoices.editForm.korExemptionLabel')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Form.Group>

                                {/* BTW rate — hidden when KOR is on */}
                                {!profile.kor_exemption && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">{t('invoices.editForm.btwRate')}</Form.Label>
                                        <div className="d-flex gap-2">
                                            {[['9', '9%'], ['21', '21%'], ['mixed', t('invoices.editForm.btwRateMixed')]].map(([val, label]) => (
                                                <Button
                                                    key={val}
                                                    variant={profile.btw_rate === val ? 'primary' : 'outline-secondary'}
                                                    size="sm"
                                                    onClick={() => handleFieldChange('btw_rate', val)}
                                                >
                                                    {label}
                                                </Button>
                                            ))}
                                        </div>
                                        <Form.Text className="text-muted">{t('invoices.editForm.btwRateHint')}</Form.Text>
                                    </Form.Group>
                                )}

                                {/* Payment terms */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">{t('invoices.editForm.paymentTerms')}</Form.Label>
                                    <FormatToolbar
                                        sizeKey="payment_terms_font_size"
                                        boldKey="payment_terms_bold"
                                        blockPositionKey="payment_terms_block_position"
                                        alignKey="payment_terms_align"
                                        profile={profile}
                                        onChange={handleFieldChange}
                                        t={t}
                                    />
                                    <Form.Control
                                        type="text"
                                        placeholder={t('invoices.editForm.paymentTermsPlaceholder')}
                                        value={profile.payment_terms}
                                        onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
                                        style={{
                                            fontSize: FONT_SIZE_MAP[profile.payment_terms_font_size] || 14,
                                            fontWeight: profile.payment_terms_bold ? 700 : 400,
                                            textAlign: profile.payment_terms_align || 'left',
                                        }}
                                    />
                                    <Form.Text className="text-muted">{t('invoices.editForm.paymentTermsHint')}</Form.Text>
                                </Form.Group>

                                {/* Invoice notes */}
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-semibold">
                                        {t('invoices.editForm.invoiceNotes')} <span className="text-muted fw-normal">— {t('invoices.editForm.optional')}</span>
                                    </Form.Label>
                                    <FormatToolbar
                                        sizeKey="invoice_notes_font_size"
                                        boldKey="invoice_notes_bold"
                                        blockPositionKey="invoice_notes_block_position"
                                        alignKey="invoice_notes_align"
                                        profile={profile}
                                        onChange={handleFieldChange}
                                        t={t}
                                    />
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder={t('invoices.editForm.invoiceNotesPlaceholder')}
                                        value={profile.invoice_notes}
                                        onChange={(e) => handleFieldChange('invoice_notes', e.target.value)}
                                        style={{
                                            fontSize: FONT_SIZE_MAP[profile.invoice_notes_font_size] || 14,
                                            fontWeight: profile.invoice_notes_bold ? 700 : 400,
                                            textAlign: profile.invoice_notes_align || 'left',
                                        }}
                                    />
                                    <Form.Text className="text-muted">{t('invoices.editForm.invoiceNotesHint')}</Form.Text>
                                </Form.Group>
                            </div>

                            <hr className="my-4" />

                            {/* ── Section: Delivery Settings ── */}
                            <div className="mb-3">
                                <h6 className="text-uppercase fw-bold text-primary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.08em' }}>
                                    <i className="fa-solid fa-truck me-1" /> {t('invoices.editForm.sectionDelivery')}
                                </h6>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-semibold">{t('invoices.editForm.freeDeliveryThreshold')}</Form.Label>
                                    <div className="input-group" style={{ maxWidth: 200 }}>
                                        <span className="input-group-text">€</span>
                                        <Form.Control
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={profile.free_delivery_threshold ?? 150}
                                            onChange={(e) => handleFieldChange('free_delivery_threshold', parseFloat(e.target.value) || 0)}
                                        />
                                    </div>
                                    <Form.Text className="text-muted">{t('invoices.editForm.freeDeliveryThresholdHint')}</Form.Text>
                                </Form.Group>
                            </div>
                        </Modal.Body>

                        <Modal.Footer style={{ borderTop: '2px solid #f0e6ff' }}>
                            <Button variant="secondary" onClick={() => setEditInvoiceDataShow(false)} disabled={saving}>
                                {t('common.cancel')}
                            </Button>
                            <Button variant="primary" onClick={saveProfile} disabled={saving}>
                                {saving ? (
                                    <><Spinner animation="border" size="sm" className="me-1" />{t('common.loading')}</>
                                ) : (
                                    <><i className="fa-solid fa-floppy-disk me-1" />{t('common.save')}</>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
            </div>
        </div>
    );
}
